/**
 * ColumnMappingGrid — Edit the column_mappings section of a job.
 *
 * One row per mapping: src_expression (monospace), Transformation note,
 * tgt_col, data_type (with optional length / precision / scale
 * qualifiers), wand icon (NL→SQL — Epic 6), delete (hover-revealed).
 *
 * "Auto-Map Columns" (re)generates the grid by fuzzy-matching the job's
 * driver source table (Source tab) against whatever target_table is
 * currently saved (Target tab) — deterministic, no LLM — mirroring the
 * source/transformation/target shape Data Converter's STTM mapping grid
 * uses (agents/data_converter/patterns.py's SttmFieldMapping). Populates
 * the grid as a pending (dirty) preview — still requires "Save Column
 * Mappings" to persist, same as any other manual edit.
 *
 * Data-type qualifiers are shown inline next to the type dropdown:
 *   - VARCHAR / CHAR / NVARCHAR / NCHAR / BINARY / VARBINARY → Length input
 *   - DECIMAL / NUMERIC                                       → Precision + Scale inputs
 *
 * The fully-qualified type string (e.g. "VARCHAR(255)", "DECIMAL(18,2)")
 * is assembled from the base type + qualifier fields before PATCH.
 *
 * Changes call PATCH .../column-mappings.
 *
 * Custom SQL override: a checkbox lets the user write their own SQL that
 * replaces the auto-generated mapping entirely — it is pushed down and
 * executed directly against the source (see sql_generator.py's
 * source_sql_override handling). Saved via PUT .../jobs/{id} (a plain job
 * field, not a JSONB section) since it is independent from column_mappings
 * — the target_col/data_type rows below still define the expected output
 * shape; the override SQL's result columns must be aliased to match them.
 * The grid (and Save Column Mappings) stay enabled while the override is
 * active — only source_expression is ignored — so those rows can still be
 * edited directly. "Auto-Map Columns from SQL" (shown once the override
 * checkbox is on) populates them automatically instead: it parses the
 * override SQL's own `AS alias` column list and fuzzy-matches each alias
 * onto the target table's real columns (deterministic, no LLM, no query
 * execution — see auto_mapper.py's extract_select_aliases()/
 * match_sql_override_to_target()), replacing the "Auto-Map Columns" (driver
 * source table) tool which doesn't apply once the override SQL has
 * replaced the driver table entirely.
 */

import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Wand2, Wand } from "lucide-react";
import type {
  PEJob,
  ColumnMapping,
  ColumnMappingsPayload,
} from "../../../features/pipeline-engineer/types";
import {
  patchColumnMappings,
  autoMapJobColumns,
  autoMapJobColumnsFromSql,
  updateJob,
} from "../../../features/pipeline-engineer/api/pe-client";
import { useIntrospectTask } from "../../../features/pipeline-engineer/useAgenticApi";
import { splitSchemaTable } from "../../../features/pipeline-engineer/schemaTable";
import { NLToSQLPopover } from "./NLToSQLPopover";

// ---------------------------------------------------------------------------
// Type modifier helpers
// ---------------------------------------------------------------------------

/** Types that accept a single length modifier: TYPE(n) e.g. VARCHAR(255). */
const LENGTH_TYPES: ReadonlySet<string> = new Set([
  "VARCHAR",
  "CHAR",
  "NVARCHAR",
  "NCHAR",
  "BINARY",
  "VARBINARY",
]);

/** Types that accept precision + optional scale: TYPE(p,s) e.g. DECIMAL(18,2). */
const PRECISION_SCALE_TYPES: ReadonlySet<string> = new Set([
  "DECIMAL",
  "NUMERIC",
]);

interface ParsedType {
  /** Base type name in UPPERCASE, e.g. "VARCHAR". */
  baseType: string;
  /** Length qualifier for VARCHAR/CHAR etc., e.g. "255". Empty string when absent. */
  length: string;
  /** Precision for DECIMAL/NUMERIC, e.g. "18". Empty string when absent. */
  precision: string;
  /** Scale for DECIMAL/NUMERIC, e.g. "2". Empty string when absent. */
  scale: string;
}

/**
 * Parse a fully-qualified type string into its components.
 * Handles TYPE(p,s), TYPE(n), and bare TYPE.
 */
function parseDataType(dataType: string): ParsedType {
  const upper = dataType.trim().toUpperCase();
  // TYPE(p,s) — precision + scale (DECIMAL(18,2), NUMERIC(10,4))
  const precScaleMatch = upper.match(/^([A-Z ]+)\(\s*(\d+)\s*,\s*(\d+)\s*\)$/);
  if (precScaleMatch) {
    return {
      baseType: precScaleMatch[1].trim(),
      length: "",
      precision: precScaleMatch[2],
      scale: precScaleMatch[3],
    };
  }
  // TYPE(n) — length only (VARCHAR(255), CHAR(10))
  const lengthMatch = upper.match(/^([A-Z ]+)\(\s*(\d+)\s*\)$/);
  if (lengthMatch) {
    return {
      baseType: lengthMatch[1].trim(),
      length: lengthMatch[2],
      precision: "",
      scale: "",
    };
  }
  // Bare type — no qualifier
  return { baseType: upper, length: "", precision: "", scale: "" };
}

/**
 * Assemble a fully-qualified type string from its components.
 * Returns e.g. "VARCHAR(255)", "DECIMAL(18,2)", or bare "BIGINT".
 */
function assembleDataType({
  baseType,
  length,
  precision,
  scale,
}: ParsedType): string {
  const bt = baseType.toUpperCase();
  if (LENGTH_TYPES.has(bt) && length.trim()) return `${bt}(${length.trim()})`;
  if (PRECISION_SCALE_TYPES.has(bt)) {
    if (precision.trim() && scale.trim())
      return `${bt}(${precision.trim()},${scale.trim()})`;
    if (precision.trim()) return `${bt}(${precision.trim()})`;
  }
  return bt;
}

// ---------------------------------------------------------------------------
// Trailing-alias detection
// ---------------------------------------------------------------------------

/**
 * Matches a trailing `AS <alias>` clause (bare, quoted, or bracketed
 * identifier) at the very end of a pasted SQL fragment, e.g. the
 * `AS Test_Value` in `CASE WHEN ... END AS Test_Value`.
 */
const TRAILING_ALIAS_RE =
  /\s+AS\s+(?:"([^"]+)"|`([^`]+)`|\[([^\]]+)\]|([A-Za-z_][A-Za-z0-9_]*))\s*;?\s*$/i;

/**
 * Split a pasted `expression AS alias` fragment into its two parts.
 *
 * The SQL generator always appends its own `AS {tgt_col}` when building the
 * SELECT list (see `agents/pipeline_engineer/engine/sql_generator.py`), so a
 * Source Expression that already ends in its own `AS alias` would otherwise
 * produce an invalid doubled `... AS alias AS "alias"` and fail at the
 * source engine. Users naturally paste a complete expression including its
 * alias (copied from elsewhere, or typed following ordinary SQL syntax), so
 * this is detected on blur and split: the alias becomes the suggested
 * Target Column (only when that field is still empty) and the trailing
 * `AS alias` is stripped from the expression itself.
 */
function splitTrailingAlias(expr: string): {
  expression: string;
  alias: string | null;
} {
  const match = TRAILING_ALIAS_RE.exec(expr);
  if (!match) return { expression: expr, alias: null };
  const alias = match[1] ?? match[2] ?? match[3] ?? match[4] ?? null;
  return { expression: expr.slice(0, match.index).trimEnd(), alias };
}

/**
 * Heuristic: does this text look like it's actually SQL (rather than a
 * short human-readable note)? Used to warn when a real expression was
 * pasted into the documentation-only Transformation field instead of
 * Source Expression — the exact mistake that produces an "invalid column"
 * error at run time, since Transformation is never read by the SQL
 * generator.
 */
const SQL_LOOKING_RE =
  /\bcase\s+when\b|\bselect\b|\bconcat\s*\(|\bcast\s*\(|\bcoalesce\s*\(|\bsubstring\s*\(|[A-Za-z_][\w]*\.[A-Za-z_][\w]*\s*=/i;

function looksLikeSqlExpression(text: string): boolean {
  return SQL_LOOKING_RE.test(text);
}

// ---------------------------------------------------------------------------
// Internal row type (no derived `dataType` field — always assembled on use)
// ---------------------------------------------------------------------------

interface MappingRow {
  id: string;
  srcExpression: string;
  tgtCol: string;
  /** Base type name (uppercase), e.g. "VARCHAR", "DECIMAL", "BIGINT". */
  baseType: string;
  /** Length for VARCHAR/CHAR/BINARY types, e.g. "255". Empty when absent. */
  typeLength: string;
  /** Precision for DECIMAL/NUMERIC, e.g. "18". Empty when absent. */
  typePrecision: string;
  /** Scale for DECIMAL/NUMERIC, e.g. "2". Empty when absent. */
  typeScale: string;
  description: string;
  isWatermark: boolean;
  nullable: boolean;
}

/** Derive the fully-qualified data_type string for a row. */
const fullType = (r: MappingRow): string =>
  assembleDataType({
    baseType: r.baseType,
    length: r.typeLength,
    precision: r.typePrecision,
    scale: r.typeScale,
  });

function mappingToRow(m: ColumnMapping, idx: number): MappingRow {
  const { baseType, length, precision, scale } = parseDataType(m.dataType);
  return {
    id: `cm-${idx}-${m.tgtCol}`,
    srcExpression: m.srcExpression,
    tgtCol: m.tgtCol,
    baseType,
    typeLength: length,
    typePrecision: precision,
    typeScale: scale,
    description: m.description ?? "",
    isWatermark: m.isWatermark,
    nullable: m.nullable,
  };
}

function rowsToPayload(rows: MappingRow[]): ColumnMappingsPayload {
  return {
    column_mappings: rows.map((r) => ({
      src_expression: r.srcExpression,
      tgt_col: r.tgtCol,
      data_type: fullType(r),
      description: r.description || null,
      is_watermark: r.isWatermark,
      nullable: r.nullable,
    })),
  };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CELL_INPUT =
  "w-full px-2 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-white placeholder-white/30 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30";

const MONO_CELL_INPUT = `${CELL_INPUT} font-mono`;

const QUALIFIER_INPUT =
  "px-1.5 py-1.5 bg-white/5 border border-white/10 rounded text-xs text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 text-center";

/** Common SQL data types for the data-type dropdown.
 *  Values are lowercase to match Playwright selectOption() calls in e2e specs. */
const DATA_TYPES = [
  "string",
  "varchar",
  "char",
  "nvarchar",
  "nchar",
  "text",
  "boolean",
  "tinyint",
  "smallint",
  "int",
  "integer",
  "bigint",
  "float",
  "double",
  "decimal",
  "numeric",
  "date",
  "datetime",
  "timestamp",
  "time",
  "binary",
  "varbinary",
  "json",
  "array",
  "struct",
  "uuid",
] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ColumnMappingGridProps {
  job: PEJob;
  /** Project ID — passed to NLToSQLPopover for LLM provider resolution. */
  projectId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (dirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ColumnMappingGrid({
  job,
  projectId,
  onSaved,
  onDirtyChange,
  onConflict,
}: ColumnMappingGridProps) {
  const [rows, setRows] = useState<MappingRow[]>(() =>
    job.columnMappings.map(mappingToRow),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * Row id that currently has the NL→SQL popover open.
   * Only one popover can be open at a time.
   */
  const [nlPopoverRowId, setNlPopoverRowId] = useState<string | null>(null);

  // ── Custom SQL override — replaces the grid-driven mapping entirely ──
  const [useSqlOverride, setUseSqlOverride] = useState<boolean>(
    () => !!job.sourceSqlOverride,
  );
  const [overrideSql, setOverrideSql] = useState<string>(
    () => job.sourceSqlOverride ?? "",
  );
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideSaveError, setOverrideSaveError] = useState<string | null>(
    null,
  );

  // ── Auto-Map Columns — fuzzy-match driver source table onto job.targetTable ──
  // start() only triggers introspection and returns before it completes —
  // actual completion arrives later via the hooks' own polling effects, so
  // we track "waiting" separately and run the map once both go terminal.
  const [autoMapLoading, setAutoMapLoading] = useState(false);
  const [autoMapError, setAutoMapError] = useState<string | null>(null);
  const [waitingForIntrospect, setWaitingForIntrospect] = useState(false);
  const sourceIntrospect = useIntrospectTask(
    job.pipelineId,
    projectId,
    "source",
  );
  const targetIntrospect = useIntrospectTask(
    job.pipelineId,
    projectId,
    "target",
  );

  const performAutoMap = useCallback(async () => {
    const sTaskId = sourceIntrospect.taskId;
    const tTaskId = targetIntrospect.taskId;
    if (sTaskId === null || tTaskId === null) {
      setAutoMapError("Introspection did not return a task id.");
      setAutoMapLoading(false);
      return;
    }
    try {
      const { columnMappings } = await autoMapJobColumns(
        job.id,
        sTaskId,
        tTaskId,
        job.targetTable,
      );
      // Populate as a pending preview — user reviews then clicks
      // "Save Column Mappings" explicitly, same as any manual edit.
      setRows(columnMappings.map(mappingToRow));
    } catch (err) {
      setAutoMapError(err instanceof Error ? err.message : "Auto-map failed.");
    } finally {
      setAutoMapLoading(false);
    }
  }, [
    job.id,
    job.targetTable,
    sourceIntrospect.taskId,
    targetIntrospect.taskId,
  ]);

  const handleAutoMap = useCallback(() => {
    setAutoMapError(null);
    setAutoMapLoading(true);
    if (
      sourceIntrospect.task?.status === "completed" &&
      targetIntrospect.task?.status === "completed"
    ) {
      void performAutoMap();
    } else {
      setWaitingForIntrospect(true);
      // Introspect the job's OWN driver table / target table schema rather
      // than relying on the pipeline's single default schema — a job reused
      // from a Data Converter mapping may have a driver table / target table
      // in a schema that differs from the pipeline's default (e.g. the
      // mapping's "Sales.SpecialOffer" vs. the pipeline's default "dbo").
      const driverTable = job.sourceTables.find((t) => t.isDriver)?.table ?? "";
      const sourceSchema = splitSchemaTable(driverTable).schema || null;
      const targetSchema = splitSchemaTable(job.targetTable).schema || null;
      void sourceIntrospect.start(null, sourceSchema);
      void targetIntrospect.start(null, targetSchema);
    }
  }, [
    sourceIntrospect,
    targetIntrospect,
    performAutoMap,
    job.sourceTables,
    job.targetTable,
  ]);

  // Fires once both introspections reach a terminal status while waiting.
  useEffect(() => {
    if (!waitingForIntrospect) return;
    const sStatus = sourceIntrospect.task?.status;
    const tStatus = targetIntrospect.task?.status;
    if (sStatus === "completed" && tStatus === "completed") {
      setWaitingForIntrospect(false);
      void performAutoMap();
    } else if (sStatus === "failed" || tStatus === "failed") {
      setWaitingForIntrospect(false);
      setAutoMapLoading(false);
      setAutoMapError(
        sourceIntrospect.error ??
          targetIntrospect.error ??
          "Introspection failed.",
      );
    }
  }, [
    waitingForIntrospect,
    sourceIntrospect.task,
    targetIntrospect.task,
    sourceIntrospect.error,
    targetIntrospect.error,
    performAutoMap,
  ]);

  // ── Auto-Map Columns from SQL — parse the override SQL's own SELECT-list
  // aliases and fuzzy-match them onto job.targetTable's real columns. Only
  // the target side needs introspecting (the override SQL replaces the
  // driver source table entirely, so there is nothing to introspect there).
  const [sqlAutoMapLoading, setSqlAutoMapLoading] = useState(false);
  const [sqlAutoMapError, setSqlAutoMapError] = useState<string | null>(null);
  const [waitingForTargetOnly, setWaitingForTargetOnly] = useState(false);

  const performAutoMapFromSql = useCallback(async () => {
    const tTaskId = targetIntrospect.taskId;
    if (tTaskId === null) {
      setSqlAutoMapError("Introspection did not return a task id.");
      setSqlAutoMapLoading(false);
      return;
    }
    try {
      const { columnMappings } = await autoMapJobColumnsFromSql(
        job.id,
        tTaskId,
        job.targetTable,
        overrideSql,
      );
      setRows(columnMappings.map(mappingToRow));
    } catch (err) {
      setSqlAutoMapError(
        err instanceof Error ? err.message : "Auto-map from SQL failed.",
      );
    } finally {
      setSqlAutoMapLoading(false);
    }
  }, [job.id, job.targetTable, overrideSql, targetIntrospect.taskId]);

  const handleAutoMapFromSql = useCallback(() => {
    setSqlAutoMapError(null);
    if (!overrideSql.trim()) {
      setSqlAutoMapError("Enter the override SQL first.");
      return;
    }
    setSqlAutoMapLoading(true);
    if (targetIntrospect.task?.status === "completed") {
      void performAutoMapFromSql();
    } else {
      setWaitingForTargetOnly(true);
      const targetSchema = splitSchemaTable(job.targetTable).schema || null;
      void targetIntrospect.start(null, targetSchema);
    }
  }, [overrideSql, targetIntrospect, performAutoMapFromSql, job.targetTable]);

  // Fires once the target introspection reaches a terminal status while waiting.
  useEffect(() => {
    if (!waitingForTargetOnly) return;
    const tStatus = targetIntrospect.task?.status;
    if (tStatus === "completed") {
      setWaitingForTargetOnly(false);
      void performAutoMapFromSql();
    } else if (tStatus === "failed") {
      setWaitingForTargetOnly(false);
      setSqlAutoMapLoading(false);
      setSqlAutoMapError(targetIntrospect.error ?? "Introspection failed.");
    }
  }, [
    waitingForTargetOnly,
    targetIntrospect.task,
    targetIntrospect.error,
    performAutoMapFromSql,
  ]);

  // Compare rows against the original job (using assembled types both sides) to
  // detect unsaved changes. Avoids permanent-dirty bug from case normalisation.
  // Also folds in the SQL override's own unsaved-changes state (toggling it
  // on/off, or editing its text, counts as dirty too).
  useEffect(() => {
    const pick = (r: MappingRow) => ({
      srcExpression: r.srcExpression,
      tgtCol: r.tgtCol,
      dataType: fullType(r),
      description: r.description || null,
      isWatermark: r.isWatermark,
    });
    const original = JSON.stringify(
      job.columnMappings.map(mappingToRow).map(pick),
    );
    const current = JSON.stringify(rows.map(pick));
    const overrideDirty = useSqlOverride
      ? overrideSql !== (job.sourceSqlOverride ?? "")
      : !!job.sourceSqlOverride;
    onDirtyChange(original !== current || overrideDirty);
  }, [
    rows,
    job.columnMappings,
    job.sourceSqlOverride,
    useSqlOverride,
    overrideSql,
    onDirtyChange,
  ]);

  useEffect(() => {
    setRows(job.columnMappings.map(mappingToRow));
    setUseSqlOverride(!!job.sourceSqlOverride);
    setOverrideSql(job.sourceSqlOverride ?? "");
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = useCallback((id: string, patch: Partial<MappingRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        id: `cm-new-${Date.now()}`,
        srcExpression: "",
        tgtCol: "",
        baseType: "STRING",
        typeLength: "",
        typePrecision: "",
        typeScale: "",
        description: "",
        isWatermark: false,
        nullable: true,
      },
    ]);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSaving(true);
    try {
      let updated = await patchColumnMappings(
        job.pipelineId,
        job.id,
        rowsToPayload(rows),
      );
      // If the user unchecked "Use custom SQL to override mapping" and saved
      // from here instead of clicking "Save SQL Override", the override
      // would otherwise stay set server-side — re-enabling the checkbox the
      // next time this job's data reloads (e.g. switching tabs and back).
      // Clear it explicitly so switching back to default mapping sticks.
      if (!useSqlOverride && job.sourceSqlOverride) {
        updated = await updateJob(job.pipelineId, job.id, {
          source_sql_override: "",
        });
      }
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [
    rows,
    job.pipelineId,
    job.id,
    job.sourceSqlOverride,
    useSqlOverride,
    onSaved,
    onConflict,
  ]);

  // Saves the custom SQL override via PUT .../jobs/{id} (a plain job field,
  // not a JSONB section — unlike column_mappings). Sends "" (not null) to
  // explicitly clear a previously-set override when the checkbox is
  // unchecked, matching the backend's generic-update-loop contract (it skips
  // any field whose value is None, so null can never clear a field there).
  const handleSaveOverride = useCallback(async () => {
    setOverrideSaveError(null);
    setOverrideSaving(true);
    try {
      const updated = await updateJob(job.pipelineId, job.id, {
        source_sql_override: useSqlOverride ? overrideSql : "",
      });
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409") || msg.toLowerCase().includes("conflict")) {
        onConflict();
      } else {
        setOverrideSaveError(msg);
      }
    } finally {
      setOverrideSaving(false);
    }
  }, [
    job.pipelineId,
    job.id,
    useSqlOverride,
    overrideSql,
    onSaved,
    onConflict,
  ]);

  return (
    <div className="space-y-4">
      {/* Auto-Map Columns (name-similarity, driver source table → target
          table) — disabled whenever the custom SQL override below is
          active, since the override SQL replaces the driver source table
          entirely; "Auto-Map Columns from SQL" in the override section
          below is the equivalent tool while override is active. The grid
          and Save button further down stay enabled either way — target_col
          and data_type are still needed to define the override's expected
          output shape. */}
      <fieldset
        disabled={useSqlOverride}
        className={`space-y-2 ${useSqlOverride ? "opacity-40 pointer-events-none" : ""}`}
      >
        <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-white/40">Target table:</span>
              <span className="text-xs font-mono text-white/80 truncate">
                {job.targetTable}
              </span>
            </div>
            <button
              type="button"
              onClick={handleAutoMap}
              disabled={autoMapLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {autoMapLoading ? (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Wand size={12} aria-hidden="true" />
              )}
              Auto-Map Columns
            </button>
          </div>

          {(sourceIntrospect.error ||
            targetIntrospect.error ||
            autoMapError) && (
            <p className="text-red-400 text-xs" role="alert">
              {sourceIntrospect.error || targetIntrospect.error || autoMapError}
            </p>
          )}

          <p className="text-white/30 text-[10px]">
            Matches the driver source table (Source tab) onto {job.targetTable}
            &apos;s real columns by name similarity — no LLM. Change the target
            table in the Target tab, then re-run this to refresh the mapping.
          </p>
        </div>
      </fieldset>

      {useSqlOverride && (
        <p className="text-amber-400/80 text-[10px] px-2">
          SQL override is active —{" "}
          <span className="font-mono">source_expression</span> below is ignored;
          only <span className="font-mono">target_col</span> and{" "}
          <span className="font-mono">data_type</span> are used, to define the
          expected output shape of the override SQL below. Use{" "}
          <span className="font-mono">Auto-Map Columns from SQL</span> in the
          SQL override section to populate them from the query itself.
        </p>
      )}

      <div className="space-y-4">
        {/* Grid header */}
        <div className="grid grid-cols-[2fr_1.5fr_1.5fr_2fr_0.8fr_auto] gap-2 px-2">
          <span className="text-xs font-medium text-white/40 font-mono">
            source_expression
          </span>
          <span className="text-xs font-medium text-white/40">
            transformation
          </span>
          <span className="text-xs font-medium text-white/40">target_col</span>
          <span className="text-xs font-medium text-white/40">
            data_type
            <span className="text-white/20 font-normal ml-1">
              (length · prec, scale)
            </span>
          </span>
          <span className="text-xs font-medium text-white/40">nullable</span>
          <span className="w-16" />
        </div>

        {/* Rows */}
        <div className="space-y-1.5">
          {rows.map((row, idx) => {
            const bt = row.baseType.toUpperCase();
            const needsLength = LENGTH_TYPES.has(bt);
            const needsPrecScale = PRECISION_SCALE_TYPES.has(bt);
            const transformationLooksLikeSql =
              row.description.trim() !== "" &&
              looksLikeSqlExpression(row.description) &&
              !looksLikeSqlExpression(row.srcExpression);

            return (
              <div key={row.id} className="space-y-1">
                <div className="group grid grid-cols-[2fr_1.5fr_1.5fr_2fr_0.8fr_auto] gap-2 items-center px-2">
                  {/* Source expression — the ONLY field actually used to build
                  SQL (see src_expression in sql_generator.py). Supports any
                  valid SQL expression, including multi-table CASE WHEN. */}
                  <input
                    type="text"
                    aria-label={`Source expression for row ${idx + 1}`}
                    className={MONO_CELL_INPUT}
                    placeholder="col or expression"
                    value={row.srcExpression}
                    onChange={(e) =>
                      updateRow(row.id, { srcExpression: e.target.value })
                    }
                    onBlur={(e) => {
                      const { expression, alias } = splitTrailingAlias(
                        e.target.value,
                      );
                      if (alias === null) return;
                      updateRow(row.id, {
                        srcExpression: expression,
                        ...(row.tgtCol.trim() === "" ? { tgtCol: alias } : {}),
                      });
                    }}
                  />

                  {/* Transformation — free-text note describing the transform
                  (e.g. "Direct copy", "UPPER(name)", "cents -> dollars").
                  Purely descriptive: the actual SQL lives in src_expression
                  above; this mirrors Data Converter's separate
                  source/transformation columns for reviewability. */}
                  <input
                    type="text"
                    aria-label={`Transformation for row ${idx + 1}`}
                    className={CELL_INPUT}
                    placeholder="Direct copy"
                    title="Documentation only — the SQL that actually runs is the Source Expression field on the left."
                    value={row.description}
                    onChange={(e) =>
                      updateRow(row.id, { description: e.target.value })
                    }
                  />

                  {/* Target column */}
                  <input
                    type="text"
                    aria-label={`Target column for row ${idx + 1}`}
                    className={CELL_INPUT}
                    placeholder="tgt_col"
                    value={row.tgtCol}
                    onChange={(e) =>
                      updateRow(row.id, { tgtCol: e.target.value })
                    }
                  />

                  {/* Data type: base selector + optional qualifiers */}
                  <div className="flex gap-1 items-center min-w-0">
                    {/* Base type dropdown */}
                    <select
                      aria-label={`Data type for row ${idx + 1}`}
                      className={`${CELL_INPUT} flex-1 min-w-0`}
                      value={
                        DATA_TYPES.includes(
                          row.baseType.toLowerCase() as (typeof DATA_TYPES)[number],
                        )
                          ? row.baseType.toLowerCase()
                          : "string"
                      }
                      onChange={(e) => {
                        const newBase = e.target.value.toUpperCase();
                        // Clear qualifiers when base type changes
                        updateRow(row.id, {
                          baseType: newBase,
                          typeLength: "",
                          typePrecision: "",
                          typeScale: "",
                        });
                      }}
                    >
                      {DATA_TYPES.map((dt) => (
                        <option key={dt} value={dt}>
                          {dt.toUpperCase()}
                        </option>
                      ))}
                    </select>

                    {/* Length — shown for VARCHAR, CHAR, BINARY, etc. */}
                    {needsLength && (
                      <input
                        type="number"
                        min={1}
                        max={65535}
                        aria-label={`Length for ${row.tgtCol || `column ${idx + 1}`}`}
                        title="Length (e.g. 255 for VARCHAR)"
                        placeholder="len"
                        className={`${QUALIFIER_INPUT} w-14 flex-shrink-0`}
                        value={row.typeLength}
                        onChange={(e) =>
                          updateRow(row.id, { typeLength: e.target.value })
                        }
                      />
                    )}

                    {/* Precision + Scale — shown for DECIMAL, NUMERIC */}
                    {needsPrecScale && (
                      <>
                        <input
                          type="number"
                          min={1}
                          max={38}
                          aria-label={`Precision for ${row.tgtCol || `column ${idx + 1}`}`}
                          title="Precision — total significant digits (e.g. 18)"
                          placeholder="prec"
                          className={`${QUALIFIER_INPUT} w-12 flex-shrink-0`}
                          value={row.typePrecision}
                          onChange={(e) =>
                            updateRow(row.id, { typePrecision: e.target.value })
                          }
                        />
                        <span className="text-white/20 text-xs flex-shrink-0">
                          ,
                        </span>
                        <input
                          type="number"
                          min={0}
                          max={38}
                          aria-label={`Scale for ${row.tgtCol || `column ${idx + 1}`}`}
                          title="Scale — decimal places (e.g. 2)"
                          placeholder="scale"
                          className={`${QUALIFIER_INPUT} w-12 flex-shrink-0`}
                          value={row.typeScale}
                          onChange={(e) =>
                            updateRow(row.id, { typeScale: e.target.value })
                          }
                        />
                      </>
                    )}

                    {/* Assembled type preview — shown only when qualifiers are set */}
                    {(needsLength && row.typeLength) ||
                    (needsPrecScale && row.typePrecision) ? (
                      <span
                        className="text-white/30 text-[10px] font-mono flex-shrink-0 whitespace-nowrap"
                        title={`Stored as: ${fullType(row)}`}
                      >
                        {fullType(row)}
                      </span>
                    ) : null}
                  </div>

                  <input
                    type="checkbox"
                    aria-label={`Nullable for row ${idx + 1}`}
                    checked={row.nullable}
                    onChange={(e) =>
                      updateRow(row.id, { nullable: e.target.checked })
                    }
                    className="justify-self-center rounded border-white/20 bg-white/5"
                  />

                  {/* Hover-revealed wand + delete */}
                  <div className="relative flex gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                    <button
                      type="button"
                      aria-label={`AI NL→SQL for row ${idx + 1}`}
                      title="Describe the transformation in plain English"
                      onClick={() =>
                        setNlPopoverRowId((prev) =>
                          prev === row.id ? null : row.id,
                        )
                      }
                      className="p-1.5 text-white/40 hover:text-brand-400 hover:bg-brand-500/10 rounded transition-colors"
                    >
                      <Wand2 size={13} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Remove mapping row ${idx + 1}`}
                      onClick={() => removeRow(row.id)}
                      className="p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </button>
                    {/* NL→SQL popover — one at a time */}
                    {nlPopoverRowId === row.id && (
                      <NLToSQLPopover
                        jobId={job.id}
                        projectId={projectId}
                        targetCol={row.tgtCol || `column_${idx + 1}`}
                        onExpressionSelected={(expr) => {
                          updateRow(row.id, { srcExpression: expr });
                          setNlPopoverRowId(null);
                        }}
                        onClose={() => setNlPopoverRowId(null)}
                      />
                    )}
                  </div>
                </div>

                {transformationLooksLikeSql && (
                  <div className="px-2">
                    <p className="flex items-center gap-2 text-amber-400/80 text-[10px]">
                      This looks like a SQL expression — only{" "}
                      <span className="font-mono">source_expression</span> is
                      used to build the query; Transformation is a
                      documentation-only note and is ignored.
                      <button
                        type="button"
                        onClick={() => {
                          const { expression, alias } = splitTrailingAlias(
                            row.description,
                          );
                          updateRow(row.id, {
                            srcExpression: expression,
                            ...(row.tgtCol.trim() === "" && alias !== null
                              ? { tgtCol: alias }
                              : {}),
                          });
                        }}
                        className="underline text-amber-300 hover:text-amber-200 flex-shrink-0"
                      >
                        Use as Source Expression
                      </button>
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors w-full justify-center"
        >
          <Plus size={13} aria-hidden="true" />
          Add Row
        </button>

        {saveError !== null && (
          <p className="text-red-400 text-xs" role="alert">
            {saveError}
          </p>
        )}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && (
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            )}
            Save Column Mappings
          </button>
        </div>
      </div>

      {/* Custom SQL override — replaces the grid-driven mapping entirely. */}
      <div className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useSqlOverride}
            onChange={(e) => setUseSqlOverride(e.target.checked)}
            className="rounded border-white/20 bg-white/5 text-brand-500 focus:ring-brand-500/50"
          />
          <span className="text-xs font-medium text-white/70">
            Use custom SQL to override mapping
          </span>
        </label>
        <p className="text-white/30 text-[10px]">
          Write your own SQL to pull data from the source instead of the
          auto-generated mapping below — it is pushed down and executed directly
          against the source connection (SQL pushdown). It must return one
          column per row below, aliased to match each Target Column name exactly
          (e.g. <code>AS customer_id</code>).
        </p>
        {useSqlOverride && (
          <textarea
            aria-label="Custom source SQL override"
            className={`${MONO_CELL_INPUT} w-full min-h-[160px] resize-y`}
            placeholder="SELECT o.order_id AS order_id, ... FROM orders o JOIN customers c ON ..."
            value={overrideSql}
            onChange={(e) => setOverrideSql(e.target.value)}
          />
        )}
        {useSqlOverride && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-white/30 text-[10px]">
              Parses the SQL&apos;s own <code>AS alias</code> column list and
              fuzzy-matches each alias onto {job.targetTable}&apos;s real
              columns — no LLM, no query execution. Populates the
              target_col/data_type rows above as a pending preview; still
              requires Save Column Mappings to persist.
            </p>
            <button
              type="button"
              onClick={handleAutoMapFromSql}
              disabled={sqlAutoMapLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
            >
              {sqlAutoMapLoading ? (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              ) : (
                <Wand size={12} aria-hidden="true" />
              )}
              Auto-Map Columns from SQL
            </button>
          </div>
        )}
        {sqlAutoMapError !== null && (
          <p className="text-red-400 text-xs" role="alert">
            {sqlAutoMapError}
          </p>
        )}
        {overrideSaveError !== null && (
          <p className="text-red-400 text-xs" role="alert">
            {overrideSaveError}
          </p>
        )}
        {(useSqlOverride || !!job.sourceSqlOverride) && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => void handleSaveOverride()}
              disabled={overrideSaving}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {overrideSaving && (
                <Loader2
                  size={12}
                  className="animate-spin"
                  aria-hidden="true"
                />
              )}
              Save SQL Override
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
